import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Coachmark from './tutorial/Coachmark';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './CharacterSelection.css';
import './Inventory.css';
import './Craft.css';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import InventoryBagGrid from './inventory/InventoryBagGrid';
import { ItemImage } from './inventory/inventoryBagGridUtils';
import { buildInventoryGridFromBootstrap, DEFAULT_UNLOCKED_SLOTS } from './inventory/inventoryGridBootstrap';
import { authButtonStyle, authButtonSmallStyle, textboxFrameStyle } from '../constants/authStyles';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { useCharacterOptional } from '../context/CharacterContext';
import { fetchInventoryBootstrap } from '../utils/inventoryBootstrapClient';
import { craftSetKey } from '../utils/craftComboKey';
import { formatNumber } from '../utils/localeFormat';
import { playCraftSuccessSound, playCraftFailSound } from '../utils/buttonClickSound';
import { ProfessionBadge } from './ProfessionPanels';
import { tl } from '../utils/inlineI18n';

// Çalışma > Craft: 4x4 tezgâh. Alttaki envanter, Envanter ekranındaki çantanın aynısıdır.
// Bir yığın tezgâha sürüklenince kaç adet konacağı sorulur. Tezgâhtaki malzeme kümesi tarifi,
// adetler kaç kez üretileceğini belirler; artan malzeme tezgâhta ve envanterde kalır.
// Tarifler backend/data/craft_recipes.json dosyasında.

const PUBLIC_URL = process.env.PUBLIC_URL ?? '';
const craftCssVars = {
  '--craft-slot-bg': `url(${PUBLIC_URL}/image/menu/kutukare.png)`,
  '--inv-slot-bg': `url(${PUBLIC_URL}/image/menu/kutuuzun.png)`,
  '--inv-kutukare-bg': `url(${PUBLIC_URL}/image/menu/kutukare.png)`,
};
const FLY_MS = 900;
const MIN_SWING_MS = 600;
const LEVELS = [1, 2, 3, 4, 5];
const CELLS = 16;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
const prefersReducedMotion = () => (
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
);
const flip = (n) => (n === 0 ? 0 : (n % 2) + 1);
const sortRecipes = (list) => [...list].sort((a, b) => a.level - b.level || a.name.localeCompare(b.name, 'tr'));

// Envanter yığınının craft anahtarı ("pot:1", "other:19"); tezgâha konamayan eşyalar null.
const craftKeyOf = (slotItem) => {
  if (!slotItem?.baseItem) return null;
  if (slotItem.inventoryType === 'pots') return `pot:${slotItem.baseItem.id}`;
  if (slotItem.inventoryType === 'others') return `other:${slotItem.baseItem.id}`;
  return null;
};

const cellAt = (x, y) => {
  const el = document.elementFromPoint(x, y)?.closest?.('[data-craft-cell]');
  return el ? Number(el.dataset.craftCell) : null;
};

// Hücrelerin envanter yığınlarından ayırdığı adetler (uniqueKey -> adet).
const reservedOf = (cells) => {
  const m = new Map();
  cells.forEach((c) => c?.sources.forEach((s) => m.set(s.key, (m.get(s.key) || 0) + s.count)));
  return m;
};

// Tezgâhtaki eşyaların toplamı ("pot:1" -> 101).
const totalsOf = (cells) => {
  const m = new Map();
  cells.forEach((c) => { if (c) m.set(c.item, (m.get(c.item) || 0) + c.count); });
  return m;
};

// Envanter değişince ayrılan adetleri gerçek yığınlara uydurur; olmayanları tezgâhtan çıkarır.
const fitCells = (cells, gridSlots) => {
  const stock = new Map();
  gridSlots.forEach((s) => { if (s?.uniqueKey) stock.set(s.uniqueKey, s.count || 1); });
  let changed = false;
  const out = cells.map((c) => {
    if (!c) return null;
    const sources = [];
    c.sources.forEach((s) => {
      const left = stock.get(s.key) || 0;
      const take = Math.min(left, s.count);
      if (take !== s.count) changed = true;
      if (take > 0) {
        stock.set(s.key, left - take);
        sources.push({ ...s, count: take });
      }
    });
    const count = sources.reduce((n, s) => n + s.count, 0);
    if (count <= 0) {
      changed = true;
      return null;
    }
    return count === c.count ? c : { ...c, count, sources };
  });
  return changed ? out : cells;
};

// Harcanan adetleri hücrelerden (soldan sağa, kaynak sırasıyla) düşer.
const subtractUsed = (cells, used) => {
  const need = new Map((used || []).map((u) => [u.item, u.count]));
  return cells.map((c) => {
    if (!c || !need.get(c.item)) return c;
    let n = need.get(c.item);
    const sources = c.sources.map((s) => {
      const take = Math.min(n, s.count);
      n -= take;
      return { ...s, count: s.count - take };
    }).filter((s) => s.count > 0);
    need.set(c.item, n);
    const count = sources.reduce((a, s) => a + s.count, 0);
    return count > 0 ? { ...c, count, sources } : null;
  });
};

const HammerIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="craft-icon">
    <path d="M15.2 8.8 4.6 19.4" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
    <path d="M9.84 6.38 13.38 2.84 21.16 10.62 17.62 14.16Z" fill="currentColor" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="craft-icon">
    <path d="M7 7l10 10M17 7 7 17" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="craft-icon" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12h15M13 6l6 6-6 6" />
  </svg>
);

const RecipeLine = ({ recipe, pct }) => (
  <>
    <div className="craft-recipe-items">
      {recipe.ingredients.map((s, i) => (
        <Fragment key={s.item}>
          {i > 0 ? <span className="craft-plus">+</span> : null}
          <span className="craft-mini" title={`${s.count} ${s.name}`}>
            <ItemImage imageUrl={s.imageUrl} alt={s.name} />
            <b>{s.count}</b>
          </span>
        </Fragment>
      ))}
      <span className="craft-plus craft-plus--arrow"><ArrowIcon /></span>
      <span className="craft-mini" title={`${recipe.result.count} ${recipe.result.name}`}>
        <ItemImage imageUrl={recipe.result.imageUrl} alt={recipe.result.name} />
        <b>{recipe.result.count}</b>
      </span>
    </div>
    <div className="craft-recipe-meta">
      <strong>{recipe.name}</strong>
      <span className="craft-chance">
        {pct(recipe.bonusRate > 0
          ? Math.min(Math.max(90, recipe.successRate), recipe.successRate + recipe.bonusRate)
          : recipe.successRate)}
      </span>
      {recipe.mastery > 0 ? (
        <small className="craft-mastery">{tl(`Ustalık: ${recipe.mastery} başarılı`, `Mastery: ${recipe.mastery} made`)}</small>
      ) : null}
      {recipe.catalystCovers > 0 ? (
        <small className="craft-mastery">{tl(`1 katalizör ${recipe.catalystCovers} taneye yeter`, `1 catalyst covers ${recipe.catalystCovers}`)}</small>
      ) : null}
    </div>
  </>
);

const Craft = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('craft');
  const { t: tc } = useTranslation('common');
  const { t: ti } = useTranslation('inventory');
  const username = localStorage.getItem('username');
  const { setError, setStatus, setInfo } = usePopupMessage();
  const characterCtx = useCharacterOptional();
  const refreshDepotCount = characterCtx?.refreshDepotCount;

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [gridSlots, setGridSlots] = useState([]);
  const [unlockedSlots, setUnlockedSlots] = useState(DEFAULT_UNLOCKED_SLOTS);
  const [page, setPage] = useState(1);
  const [cells, setCells] = useState(() => Array(CELLS).fill(null));
  const [busy, setBusy] = useState(false);
  const [attempting, setAttempting] = useState(false);
  const [swing, setSwing] = useState(0);
  const [shown, setShown] = useState(null);
  const [drag, setDrag] = useState(null);
  const [qty, setQty] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [bookLevel, setBookLevel] = useState(1);
  const [learned, setLearned] = useState(null);
  const [flyers, setFlyers] = useState([]);
  const [depotShown, setDepotShown] = useState(0);
  const [depotFlash, setDepotFlash] = useState(0);

  const resultRef = useRef(null);
  const depotBtnRef = useRef(null);
  const gridAreaRef = useRef(null);
  const qtyInputRef = useRef(null);
  const dragMovedRef = useRef(false);
  const tapAtRef = useRef(-1e9);
  const timersRef = useRef([]);
  const refreshDepotRef = useRef(refreshDepotCount);
  refreshDepotRef.current = refreshDepotCount;

  const later = useCallback((fn, ms) => {
    timersRef.current.push(setTimeout(fn, ms));
  }, []);
  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const pct = (n) => t('chance', { value: formatNumber(n, { maximumFractionDigits: 1 }) });
  const num = (n) => formatNumber(n);
  const errorText = (res) => t(`errors.${res?.code || 'generic'}`, {
    defaultValue: res?.message || t('errors.generic'),
    level: res?.unlockLevel ?? data?.unlockLevel ?? 1,
  });

  const loadGrid = useCallback(async () => {
    if (!username) return;
    try {
      const response = await fetchInventoryBootstrap(username);
      if (!response.data?.success) return;
      const built = await buildInventoryGridFromBootstrap({
        username,
        bootstrapData: response.data,
        equipment: response.data.equipment || null,
        t: ti,
      });
      setGridSlots(built.gridSlots);
      setUnlockedSlots(built.unlockedSlotsCount);
      setCells((c) => fitCells(c, built.gridSlots));
    } catch {
      // envanter yüklenemezse tezgâh boş kalır; hata çekiçte görünür
    }
  }, [username, ti]);

  const load = useCallback(async () => {
    if (!username) return;
    try {
      const res = await axios.get(`/api/craft/state?username=${encodeURIComponent(username)}`);
      const d = res.data;
      if (!d?.success) {
        setLoadError({ code: d?.code || 'generic', message: d?.message, unlockLevel: d?.unlockLevel });
        return;
      }
      setData(d);
      setLoadError(null);
      setDepotShown(d.depotCount);
      await loadGrid();
    } catch (err) {
      const d = err.response?.data;
      setLoadError({ code: d?.code || 'generic', message: d?.message, unlockLevel: d?.unlockLevel });
    }
  }, [username, loadGrid]);

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    load();
  }, [username, navigate, load]);

  const books = data?.books || {};
  const reserved = useMemo(() => reservedOf(cells), [cells]);
  const totals = useMemo(() => totalsOf(cells), [cells]);
  const availOf = (slotItem) => (slotItem?.count || 1) - (reserved.get(slotItem?.uniqueKey) || 0);
  const knownBySet = useMemo(() => new Map((data?.known || []).map((r) => [r.key, r])), [data?.known]);
  const failed = useMemo(() => new Set(data?.failed || []), [data?.failed]);
  const setKey = useMemo(() => craftSetKey(cells.filter(Boolean).map((c) => c.item)), [cells]);
  const known = knownBySet.get(setKey);
  const batches = known
    ? Math.min(...known.ingredients.map((ing) => Math.floor((totals.get(ing.item) || 0) / ing.count)))
    : 0;
  let status = 'empty';
  if (setKey) {
    if (known) status = batches > 0 ? 'known' : 'toofew';
    else status = failed.has(setKey) ? 'none' : 'unknown';
  }

  const handleError = (res) => {
    if (res?.code === 'missing_items') {
      setError(errorText(res));
      load();
    } else if (res?.code === 'locked') {
      setData(null);
      setLoadError({ code: 'locked', message: res.message, unlockLevel: res.unlockLevel });
    } else if (res?.code === 'all_known') {
      setInfo(errorText(res));
    } else {
      setError(errorText(res));
    }
  };

  // --- Tezgâh düzenleme (oyuncunun yaptığı her değişiklik son sonucu gizler) ---
  const edit = (next) => {
    setCells(next);
    setShown(null);
  };
  const addToCell = (index, slotItem, amount) => {
    const item = craftKeyOf(slotItem);
    const max = availOf(slotItem);
    const n = Math.max(0, Math.min(max, Math.floor(Number(amount) || 0)));
    if (!item || n <= 0) return;
    const source = { key: slotItem.uniqueKey, rowId: slotItem.id, type: slotItem.inventoryType, count: n };
    const next = cells.slice();
    const cur = next[index];
    if (cur && cur.item === item) {
      const sources = cur.sources.slice();
      const same = sources.findIndex((s) => s.key === source.key);
      if (same >= 0) sources[same] = { ...sources[same], count: sources[same].count + n };
      else sources.push(source);
      next[index] = { ...cur, count: cur.count + n, sources };
    } else {
      next[index] = {
        item,
        count: n,
        sources: [source],
        name: slotItem.baseItem.name,
        imageUrl: slotItem.baseItem.imageUrl,
      };
    }
    edit(next);
  };
  const targetFor = (item) => {
    const same = cells.findIndex((c) => c?.item === item);
    return same >= 0 ? same : cells.indexOf(null);
  };
  // Kaç adet konacağını sorar; tek adet varsa sormadan koyar.
  const askAmount = (slotItem, target) => {
    if (busy) return;
    const item = craftKeyOf(slotItem);
    if (!item) {
      setInfo(t('errors.bad_item'));
      return;
    }
    const max = availOf(slotItem);
    if (max <= 0) {
      setError(t('itemOut', { name: slotItem.baseItem.name }));
      return;
    }
    const index = target ?? targetFor(item);
    if (index < 0) {
      setInfo(t('benchFull'));
      return;
    }
    if (max === 1) {
      addToCell(index, slotItem, 1);
      return;
    }
    setQty({ slotItem, index, max, value: String(max) });
  };
  const confirmAmount = () => {
    if (!qty) return;
    addToCell(qty.index, qty.slotItem, qty.value);
    setQty(null);
  };
  // Adet penceresindeki düğmeler: dokunmatikte parmak kalkınca çalışır (klavye açıkken
  // bazı telefonlar ilk dokunuşta click üretmiyor), ardından gelen click yutulur.
  const modalTapRef = useRef(-1e9);
  // Dokunmatik bir dokunuştan hemen sonra gelen (yeni açılan pencereye düşebilen) click'i yutmak için.
  const recentTap = () => performance.now() - Math.max(tapAtRef.current, modalTapRef.current) < 700;
  const tapProps = (fn) => ({
    onPointerUp: (e) => {
      if (e.pointerType === 'mouse') return;
      modalTapRef.current = performance.now();
      fn();
    },
    onClick: () => {
      if (!recentTap()) fn();
    },
  });
  const setQtyValue = (fn) => setQty((q) => (q ? { ...q, value: String(fn(q)) } : q));
  // Pencere açılınca: telefonda klavye kendiliğinden açılmasın; fareyle çalışırken yazmaya hazır olsun.
  const qtyOpen = Boolean(qty);
  useEffect(() => {
    if (qtyOpen && window.matchMedia?.('(pointer: fine)').matches) setTimeout(() => qtyInputRef.current?.select(), 30);
  }, [qtyOpen]);

  const removeAt = (index) => {
    if (busy || !cells[index]) return;
    const next = cells.slice();
    next[index] = null;
    edit(next);
  };
  const moveCell = (from, to) => {
    if (busy || from === to) return;
    const next = cells.slice();
    const a = next[from];
    const b = next[to];
    if (a && b && a.item === b.item) {
      const sources = [...b.sources];
      a.sources.forEach((s) => {
        const i = sources.findIndex((x) => x.key === s.key);
        if (i >= 0) sources[i] = { ...sources[i], count: sources[i].count + s.count };
        else sources.push(s);
      });
      next[to] = { ...b, count: a.count + b.count, sources };
      next[from] = null;
    } else {
      next[from] = b;
      next[to] = a;
    }
    edit(next);
  };
  const missingFor = (recipe) => {
    const missing = [];
    recipe.ingredients.forEach((ing) => {
      const have = gridSlots.filter((s) => craftKeyOf(s) === ing.item).reduce((n, s) => n + (s.count || 1), 0);
      if (have < ing.count) missing.push(`${ing.count - have} ${ing.name}`);
    });
    return missing;
  };
  // Tarif defterinden "Yerleştir": bir kezlik malzemeyi tezgâha koyar.
  // Envanterdeki malzemeyle bu tariften en fazla kaç kez yapılabileceği (toplu craft).
  // Katalizör (ör. tavşan yağı) bir adediyle catalystCovers kez yapılır.
  const isCatalyst = (recipe, ing) => recipe.catalystCovers > 0 && ing.item === recipe.catalystKey;
  const needFor = (recipe, ing, times) => (isCatalyst(recipe, ing)
    ? ing.count * Math.ceil(times / recipe.catalystCovers)
    : ing.count * times);
  const maxTimesFor = (recipe) => recipe.ingredients.reduce((min, ing) => {
    const have = gridSlots.filter((s) => craftKeyOf(s) === ing.item).reduce((n, s) => n + (s.count || 1), 0);
    const sets = Math.floor(have / Math.max(1, ing.count));
    return Math.min(min, isCatalyst(recipe, ing) ? sets * recipe.catalystCovers : sets);
  }, 1000);
  const fillWith = (recipe, times = 1) => {
    const missing = missingFor(recipe);
    if (missing.length) {
      setError(t('missing', { items: missing.join(', ') }));
      return false;
    }
    const next = Array(CELLS).fill(null);
    recipe.ingredients.forEach((ing, i) => {
      let need = needFor(recipe, ing, times);
      const sources = [];
      gridSlots.forEach((s) => {
        if (need <= 0 || craftKeyOf(s) !== ing.item) return;
        const take = Math.min(need, s.count || 1);
        sources.push({ key: s.uniqueKey, rowId: s.id, type: s.inventoryType, count: take });
        need -= take;
      });
      next[i] = { item: ing.item, count: needFor(recipe, ing, times), sources, name: ing.name, imageUrl: ing.imageUrl };
    });
    edit(next);
    return true;
  };

  // --- Sürükle bırak (fare ve dokunmatik) ---
  const tapInv = (slotItem) => {
    if (!slotItem) return;
    if (slotItem.baseItem?.recipeBookLevel) readBook(slotItem.baseItem.id);
    else askAmount(slotItem, null);
  };
  const tap = (source) => {
    if (source.kind === 'cell') removeAt(source.index);
    else tapInv(source.slotItem);
  };
  const dropOn = (source, target) => {
    if (source.kind === 'inv') {
      if (target !== null) askAmount(source.slotItem, target);
    } else if (target === null) {
      removeAt(source.index); // tezgâhın dışına sürüklenen eşya envantere döner
    } else {
      moveCell(source.index, target);
    }
  };
  const tapRef = useRef(tap);
  tapRef.current = tap;
  const dropRef = useRef(dropOn);
  dropRef.current = dropOn;
  // Dokunmatikte dokunma pointerup'ta işlenir; ardından gelen click yutulur. Fare click ile çalışır.
  const clickAllowed = () => !dragMovedRef.current
    && performance.now() - Math.max(tapAtRef.current, modalTapRef.current) > 700;

  const startDrag = (event, source) => {
    if (busy || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const sx = event.clientX;
    const sy = event.clientY;
    let active = false;
    dragMovedRef.current = false;
    const move = (ev) => {
      if (!active) {
        if (Math.hypot(ev.clientX - sx, ev.clientY - sy) < 6) return;
        active = true;
        dragMovedRef.current = true;
      }
      ev.preventDefault();
      setDrag({ source, x: ev.clientX, y: ev.clientY, over: cellAt(ev.clientX, ev.clientY) });
    };
    const finish = (ev, cancelled) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      if (!active) {
        if (!cancelled && ev.pointerType !== 'mouse') {
          tapAtRef.current = performance.now();
          tapRef.current(source);
        }
        return;
      }
      setDrag(null);
      setTimeout(() => { dragMovedRef.current = false; }, 0);
      if (!cancelled) dropRef.current(source, cellAt(ev.clientX, ev.clientY));
    };
    const up = (ev) => finish(ev, false);
    const cancel = (ev) => finish(ev, true);
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
  };

  // --- Oluşan eşyanın depoya (ya da envantere) uçuşu ---
  const fly = (produced, toDepot, depotCount) => {
    const finishNow = () => {
      if (toDepot) {
        setDepotShown(depotCount);
        setDepotFlash((n) => n + 1);
        refreshDepotRef.current?.().catch?.(() => {});
      }
      setShown((s) => (s && s.outcome === 'basarili' ? { ...s, delivered: true } : s));
    };
    later(() => {
      const from = resultRef.current?.getBoundingClientRect();
      const to = (toDepot ? depotBtnRef.current : gridAreaRef.current)?.getBoundingClientRect();
      if (!from || !to || !produced.imageUrl || prefersReducedMotion()) {
        finishNow();
        return;
      }
      const id = Date.now() + Math.random();
      const startX = from.left + from.width / 2;
      const startY = from.top + from.height / 2;
      setFlyers((list) => [...list, {
        id,
        imageUrl: produced.imageUrl,
        startX,
        startY,
        deltaX: to.left + to.width / 2 - startX,
        deltaY: to.top + to.height / 2 - startY,
      }]);
      later(() => {
        setFlyers((list) => list.filter((f) => f.id !== id));
        finishNow();
      }, FLY_MS);
    }, 450);
  };

  // --- Çekiç: tezgâhtaki adetlerin yettiği kadar üretir, artan tezgâhta kalır ---
  const attempt = async () => {
    if (!data || busy || status === 'empty' || status === 'none' || status === 'toofew') return;
    const items = cells.filter(Boolean).map((c) => ({
      item: c.item,
      count: c.count,
      rows: c.sources.filter((s) => s.type === 'pots').map((s) => s.rowId),
    }));
    setBusy(true);
    setAttempting(true);
    setSwing((n) => n + 1);
    setShown(null);
    try {
      const [res] = await Promise.all([
        axios.post('/api/craft/attempt', { username, items }),
        sleep(prefersReducedMotion() ? 0 : MIN_SWING_MS),
      ]);
      const r = res.data;
      if (!r?.success) {
        handleError(r);
        return;
      }
      setData((d) => {
        if (!d) return d;
        const recipe = r.recipe;
        const knownList = recipe && !d.known.some((k) => k.id === recipe.id) ? sortRecipes([...d.known, recipe]) : d.known;
        const failedList = r.outcome === 'tarif_yok' && !d.failed.includes(r.key) ? [...d.failed, r.key] : d.failed;
        return { ...d, known: knownList, failed: failedList, depotCount: r.depotCount };
      });
      setShown(r);
      if (r.outcome === 'basarili') playCraftSuccessSound();
      else if (r.outcome === 'basarisiz' || r.outcome === 'tarif_yok') playCraftFailSound();
      if (r.consumed) setCells((c) => subtractUsed(c, r.used));
      if (r.newlyLearned && r.recipe) setStatus(t('learnedToast', { name: r.recipe.name }));
      if (r.outcome === 'basarili' && r.produced) fly(r.produced, r.toDepot, r.depotCount);
      else setDepotShown(r.depotCount);
      if (r.consumed || r.outcome === 'basarili') loadGrid();
    } catch (err) {
      handleError(err.response?.data);
    } finally {
      setBusy(false);
      setAttempting(false);
    }
  };

  // --- Tarif defteri eşyası ---
  async function readBook(otherId) {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post('/api/craft/book', { username, otherId });
      const r = res.data;
      if (!r?.success) {
        handleError(r);
        return;
      }
      setData((d) => d && { ...d, known: sortRecipes([...d.known.filter((k) => k.id !== r.recipe.id), r.recipe]) });
      setLearned(r.recipe);
      loadGrid();
    } catch (err) {
      handleError(err.response?.data);
    } finally {
      setBusy(false);
    }
  }

  // Esc ile açık pencereyi kapat.
  useEffect(() => {
    if (!bookOpen && !learned && !qty) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setLearned(null);
      setBookOpen(false);
      setQty(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bookOpen, learned, qty]);

  const goDepot = () => navigate('/inventory?tab=pending', { state: { from: '/craft' } });

  const renderToolbar = () => (
    <AuthSelectionToolbar
      className="craft-toolbar"
      onBack={() => navigate('/work')}
      backLabel={tc('back')}
      buttonStyle={authButtonStyle}
      title={t('title')}
      subtitle={t('subtitle')}
      leadingExtra={data ? (
        <>
          <button
            type="button"
            className="auth-button auth-button--compact craft-top-btn"
            style={authButtonStyle}
            onClick={() => setBookOpen(true)}
          >
            {t('recipeBook')}
            <span className="craft-top-count">{data.known.length}</span>
          </button>
          <button
            ref={depotBtnRef}
            type="button"
            className={`auth-button auth-button--compact craft-top-btn${depotFlash ? ` craft-depot-flash-${flip(depotFlash)}` : ''}`}
            style={authButtonStyle}
            data-depot-button
            onClick={goDepot}
          >
            {t('depot')}
            {depotShown > 0 ? <span className="craft-top-count">{depotShown}</span> : null}
          </button>
          <button
            type="button"
            className="craft-info-btn"
            onClick={() => setInfoOpen(true)}
            aria-label={t('infoTitle')}
            title={t('infoTitle')}
          >
            i
          </button>
        </>
      ) : null}
    />
  );

  if (!data) {
    const locked = loadError?.code === 'locked';
    return (
      <div className="auth-container auth-container--selection craft-page" style={craftCssVars}>
        <AuthLayeredCard className="craft-card">
          {renderToolbar()}
          <div className="auth-selection-body craft-body">
            <div className="craft-status-panel" style={textboxFrameStyle}>
              <p className={`craft-status-text${loadError ? ' craft-status-text--warn' : ''}`}>
                {loadError ? errorText(loadError) : t('loading')}
              </p>
            </div>
            {loadError && !locked ? (
              <div className="craft-actions">
                <button type="button" className="auth-button" style={authButtonStyle} onClick={load}>
                  {t('retry')}
                </button>
              </div>
            ) : null}
          </div>
        </AuthLayeredCard>
      </div>
    );
  }

  // --- Sağdaki sonuç kutusu ve durum yazısı ---
  const needText = (recipe) => recipe.ingredients.map((s) => `${num(s.count)} ${s.name}`).join(' + ');
  let look = 'empty';
  let slot = null;
  let caption = null;
  let message = t('statusEmpty');
  const resultImage = (stack, count) => (
    <>
      <ItemImage imageUrl={stack.imageUrl} alt={stack.name} />
      {count > 1 ? <span className="craft-count">x{num(count)}</span> : null}
    </>
  );
  if (attempting && !shown) {
    look = 'working';
    slot = <span className="craft-result-mark">…</span>;
    message = t('statusWorking');
  } else if (shown?.outcome === 'basarili' && shown.produced) {
    const p = shown.produced;
    look = shown.delivered ? 'success craft-result--delivered' : 'success';
    slot = resultImage(p, p.count);
    caption = <span className="craft-caption craft-caption--good">{shown.delivered && shown.toDepot ? t('captionDelivered') : t('captionSuccess')}</span>;
    const where = shown.toDepot ? t('toDepot') : t('toInventory');
    message = shown.attempts > 1
      ? t('statusBatchSuccess', { attempts: num(shown.attempts), successes: num(shown.successes), failures: num(shown.attempts - shown.successes), count: num(p.count), name: p.name, where })
      : t('statusSuccess', { count: num(p.count), name: p.name, where });
  } else if (shown?.outcome === 'basarisiz' && shown.recipe) {
    const r = shown.recipe;
    look = 'fail';
    slot = resultImage(r.result, 1);
    caption = <span className="craft-caption craft-caption--bad">{t('captionFail')}</span>;
    message = [
      shown.attempts > 1
        ? t('statusBatchFail', { attempts: num(shown.attempts), chance: pct(r.successRate) })
        : t('statusFail', { chance: pct(r.successRate) }),
      shown.consumed ? t('statusFailConsumed') : '',
      shown.newlyLearned ? t('statusLearned') : '',
    ].filter(Boolean).join(' ');
  } else if (shown?.outcome === 'tarif_yok' || status === 'none') {
    look = 'none';
    slot = <span className="craft-result-x"><XIcon /></span>;
    caption = <span className="craft-caption craft-caption--bad">{t('captionNone')}</span>;
    message = shown
      ? `${t('statusNoRecipe')} ${shown.consumed ? t('statusNoRecipeConsumed') : t('statusNoRecipeKept')}`
      : t('statusNone');
  } else if (status === 'toofew' && known) {
    look = 'none';
    slot = resultImage(known.result, 1);
    caption = <span className="craft-caption craft-caption--bad">{t('captionTooFew')}</span>;
    message = t('statusTooFew', { need: needText(known) });
  } else if (status === 'known' && known) {
    look = 'known';
    slot = resultImage(known.result, known.result.count * batches);
    caption = (
      <span className="craft-caption">
        <span className="craft-chance">{pct(known.successRate)}</span>
        <span className="craft-caption-name">{known.result.name}</span>
      </span>
    );
    message = [
      batches > 1
        ? t('statusKnownBatch', { batches: num(batches), chance: pct(known.successRate), count: num(known.result.count), name: known.result.name })
        : t('statusKnown', { chance: pct(known.successRate), count: num(known.result.count), name: known.result.name }),
      data.consumeOnFail ? t('statusKnownConsumeNote') : '',
    ].filter(Boolean).join(' ');
  } else if (status === 'unknown') {
    look = 'unknown';
    slot = <span className="craft-result-mark">?</span>;
    caption = <span className="craft-caption">{t('captionUnknown')}</span>;
    message = t('statusUnknown');
  }

  const bookLevels = new Set(Object.values(books));
  const levelKnown = data.known.filter((r) => r.level === bookLevel);
  const levelTotal = data.levelTotals[bookLevel] || 0;
  const hammerBlocked = status === 'none' || status === 'toofew';

  const renderSlotContent = (slotItem) => {
    if (!slotItem) return null;
    const left = availOf(slotItem);
    const isBook = Boolean(slotItem.baseItem?.recipeBookLevel);
    const craftable = Boolean(craftKeyOf(slotItem));
    return (
      <div
        className={`craft-bag-item${left <= 0 ? ' craft-bag-item--out' : ''}${craftable ? '' : ' craft-bag-item--fixed'}`}
        onPointerDown={craftable && left > 0 ? (e) => startDrag(e, { kind: 'inv', slotItem }) : undefined}
        onDragStart={(e) => e.preventDefault()}
      >
        <div className="slot-item-image">
          {slotItem.baseItem.imageUrl ? (
            <ItemImage
              imageUrl={slotItem.baseItem.imageUrl}
              alt={slotItem.baseItem.name}
              singleFrameSprite={slotItem.inventoryType === 'boots'}
            />
          ) : <div className="slot-placeholder-icon">📦</div>}
        </div>
        {left > 1 || (slotItem.count > 1 && left !== slotItem.count) ? <span className="slot-item-count">x{num(Math.max(0, left))}</span> : null}
        {isBook ? <span className="craft-read-badge">{t('readBadge')}</span> : null}
      </div>
    );
  };

  return (
    <div className={`auth-container auth-container--selection craft-page${drag ? ' craft-page--dragging' : ''}`} style={craftCssVars}>
      <Coachmark
        tourId="craft"
        steps={[
          { selector: '.craft-tabs', textKey: 'craftTabs' },
          { selector: '.craft-grid', textKey: 'craftGrid' },
      ]}
      />
      {flyers.map((f) => (
        <img
          key={f.id}
          className="craft-fly"
          src={`${PUBLIC_URL}/${String(f.imageUrl).replace(/^\//, '')}`}
          alt=""
          aria-hidden="true"
          style={{
            '--fly-start-x': `${f.startX}px`,
            '--fly-start-y': `${f.startY}px`,
            '--fly-delta-x': `${f.deltaX}px`,
            '--fly-delta-y': `${f.deltaY}px`,
          }}
        />
      ))}
      {drag ? (
        <div className="craft-ghost" style={{ left: drag.x, top: drag.y }} aria-hidden="true">
          <ItemImage imageUrl={drag.source.kind === 'inv' ? drag.source.slotItem.baseItem.imageUrl : cells[drag.source.index]?.imageUrl} alt="" />
        </div>
      ) : null}

      <AuthLayeredCard className="craft-card">
        {renderToolbar()}
        <ProfessionBadge skill="craft" />

        <div className="auth-selection-body craft-body">
          <section className="craft-bench" style={textboxFrameStyle} aria-label={t('bench')}>
            <div className="craft-grid" style={{ gridTemplateColumns: 'repeat(4, var(--craft-cell))' }}>
              {cells.map((cell, i) => (
                <button
                  key={i}
                  type="button"
                  data-craft-cell={i}
                  className={[
                    'craft-cell',
                    cell ? 'craft-cell--filled' : '',
                    drag?.over === i ? 'craft-cell--over' : '',
                    drag?.source.kind === 'cell' && drag.source.index === i ? 'craft-cell--lifted' : '',
                  ].filter(Boolean).join(' ')}
                  onPointerDown={cell ? (e) => startDrag(e, { kind: 'cell', index: i }) : undefined}
                  onDragStart={(e) => e.preventDefault()}
                  onClick={() => { if (clickAllowed() && cell) removeAt(i); }}
                  disabled={busy}
                  aria-label={cell ? t('cellFilled', { name: cell.name, count: cell.count }) : t('cellEmpty', { index: i + 1 })}
                >
                  {cell ? (
                    <>
                      <ItemImage imageUrl={cell.imageUrl} alt={cell.name} />
                      {cell.count > 1 ? <span className="craft-count">x{num(cell.count)}</span> : null}
                    </>
                  ) : null}
                </button>
              ))}
            </div>

            <div className="craft-side">
              <div className="craft-hammer-slot">
                {status !== 'empty' ? (
                  <button
                    type="button"
                    className={`craft-hammer${hammerBlocked ? ' craft-hammer--blocked' : ''}${swing ? ` craft-hammer--swing-${flip(swing)}` : ''}`}
                    style={authButtonSmallStyle}
                    onClick={attempt}
                    disabled={busy || hammerBlocked}
                    aria-label={hammerBlocked ? t('hammerBlocked') : t('hammer')}
                    title={hammerBlocked ? t('hammerBlocked') : t('hammer')}
                  >
                    <HammerIcon />
                  </button>
                ) : null}
                {hammerBlocked ? <span className="craft-hammer-x" aria-hidden="true"><XIcon /></span> : null}
              </div>
              <span className="craft-arrow" aria-hidden="true"><ArrowIcon /></span>
              <div className="craft-result-col">
                <div ref={resultRef} className={`craft-result craft-result--${look}`} aria-label={t('result')}>
                  {slot}
                </div>
                <div className="craft-result-caption">{caption}</div>
              </div>
            </div>
          </section>

          {message !== t('statusEmpty') ? (
          <div className="craft-status-panel" style={textboxFrameStyle}>
            <p className={`craft-status-text${look === 'none' || look === 'fail' ? ' craft-status-text--warn' : ''}${look.startsWith('success') ? ' craft-status-text--good' : ''}`} aria-live="polite">
              {message}
            </p>
          </div>
          ) : null}

          {status !== 'empty' && !busy ? (
            <div className="craft-actions">
              <button type="button" className="auth-button auth-button--compact" style={authButtonStyle} onClick={() => edit(Array(CELLS).fill(null))}>
                {t('clear')}
              </button>
            </div>
          ) : null}

          <div className="inventory-list craft-inventory-section">
            <InventoryBagGrid
              gridSlots={gridSlots}
              currentInventoryPage={page}
              onPageChange={setPage}
              unlockedSlotsCount={unlockedSlots}
              draggable={false}
              onClick={(event, slotItem) => { if (clickAllowed()) tapInv(slotItem); }}
              slotKeyPrefix="craft-inventory"
              wrapperClassName="craft-inventory-grid-wrapper"
              areaRef={gridAreaRef}
              emptySlotTitle={ti('slotEmpty')}
              lockedSlotTitle={ti('slotLocked')}
              renderSlotContent={renderSlotContent}
            />
          </div>
        </div>
      </AuthLayeredCard>
      {infoOpen ? (
        <div className="popup-overlay" onClick={() => setInfoOpen(false)} role="presentation">
          <AuthLayeredCard className="craft-info-popup" onClick={(e) => e.stopPropagation()}>
            <h3 className="craft-info-title">{t('infoTitle')}</h3>
            <p>{t('statusEmpty')}</p>
            <p>{t('inventoryHint')}</p>
            <p>{t('infoMastery')}</p>
            <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => setInfoOpen(false)}>
              {t('infoClose')}
            </button>
          </AuthLayeredCard>
        </div>
      ) : null}

      {qty ? (
        <div className="craft-modal-backdrop" onClick={() => { if (!recentTap()) setQty(null); }} role="presentation">
          <form
            className="craft-modal craft-modal--qty"
            role="dialog"
            aria-modal="true"
            aria-label={t('qtyTitle')}
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => { e.preventDefault(); confirmAmount(); }}
          >
            <div className="craft-qty-head">
              <span className="craft-mini craft-mini--big">
                <ItemImage imageUrl={qty.slotItem.baseItem.imageUrl} alt="" />
              </span>
              <div>
                <h3 className="craft-heading">{qty.slotItem.baseItem.name}</h3>
                <p className="craft-muted">{t('qtyQuestion', { max: num(qty.max) })}</p>
              </div>
            </div>
            <div className="craft-qty-row">
              <button
                type="button"
                className="auth-button auth-button--compact craft-qty-step"
                style={authButtonSmallStyle}
                {...tapProps(() => setQtyValue((q) => Math.max(1, (Number(q.value) || 0) - 1)))}
              >
                −
              </button>
              <input
                ref={qtyInputRef}
                id="craft-qty-input"
                className="craft-qty-input"
                type="number"
                inputMode="numeric"
                min={1}
                max={qty.max}
                value={qty.value}
                onChange={(e) => setQty((q) => ({ ...q, value: e.target.value }))}
              />
              <button
                type="button"
                className="auth-button auth-button--compact craft-qty-step"
                style={authButtonSmallStyle}
                {...tapProps(() => setQtyValue((q) => Math.min(q.max, (Number(q.value) || 0) + 1)))}
              >
                +
              </button>
            </div>
            <div className="craft-actions">
              <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} {...tapProps(() => setQtyValue(() => 1))}>1</button>
              <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} {...tapProps(() => setQtyValue((q) => Math.max(1, Math.floor(q.max / 2))))}>{t('qtyHalf')}</button>
              <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} {...tapProps(() => setQtyValue((q) => q.max))}>{t('qtyAll')}</button>
            </div>
            <div className="craft-actions">
              <button type="button" className="auth-button auth-button--compact" style={authButtonStyle} {...tapProps(() => setQty(null))}>{t('cancel')}</button>
              <button
                type="button"
                className="auth-button auth-button--compact craft-qty-place"
                style={authButtonStyle}
                {...tapProps(confirmAmount)}
                disabled={!(Number(qty.value) >= 1)}
              >
                {t('qtyPlace', { count: num(Math.min(qty.max, Math.max(0, Math.floor(Number(qty.value) || 0)))) })}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {bookOpen ? (
        <div className="craft-modal-backdrop" onClick={() => setBookOpen(false)} role="presentation">
          <div className="craft-modal" role="dialog" aria-modal="true" aria-label={t('bookTitle')} onClick={(e) => e.stopPropagation()}>
            <button type="button" className="craft-modal-close" onClick={() => setBookOpen(false)} aria-label={t('close')}>X</button>
            <h3 className="craft-heading">{t('bookTitle')}</h3>
            <div className="craft-tabs" role="tablist">
              {LEVELS.map((lvl) => {
                const total = data.levelTotals[lvl] || 0;
                const have = data.known.filter((r) => r.level === lvl).length;
                return (
                  <button
                    key={lvl}
                    type="button"
                    role="tab"
                    aria-selected={bookLevel === lvl}
                    className={`auth-button auth-button--compact craft-tab${bookLevel === lvl ? ' craft-tab--active' : ''}`}
                    style={authButtonSmallStyle}
                    onClick={() => setBookLevel(lvl)}
                  >
                    {t('bookLevelTab', { level: lvl })}
                    <small>{have}/{total}</small>
                  </button>
                );
              })}
            </div>
            <ul className="craft-recipes">
              {levelKnown.map((r) => {
                const missing = missingFor(r);
                return (
                  <li key={r.id} className="craft-recipe">
                    <div className="craft-recipe-main"><RecipeLine recipe={r} pct={pct} /></div>
                    <button
                      type="button"
                      className="auth-button auth-button--compact"
                      style={authButtonStyle}
                      disabled={missing.length > 0 || busy}
                      title={missing.length ? t('missing', { items: missing.join(', ') }) : undefined}
                      onClick={() => { if (fillWith(r)) setBookOpen(false); }}
                    >
                      {missing.length ? t('bookMissing') : t('bookPlace')}
                    </button>
                    {!missing.length && maxTimesFor(r) > 1 ? (
                      <button
                        type="button"
                        className="auth-button auth-button--compact"
                        style={authButtonStyle}
                        disabled={busy}
                        title={tl('Envanterdeki malzemenin yettiği kadar yerleştirir; çekiçle hepsi bir kerede denenir', 'Places as many sets as you have; one hammer tries them all')}
                        onClick={() => { if (fillWith(r, maxTimesFor(r))) setBookOpen(false); }}
                      >
                        {tl('Toplu', 'Bulk')} x{maxTimesFor(r)}
                      </button>
                    ) : null}
                  </li>
                );
              })}
              <li className="craft-muted">
                {levelTotal - levelKnown.length <= 0
                  ? (levelTotal ? t('bookAllKnownLevel') : t('bookNoRecipes'))
                  : `${levelKnown.length === 0 ? t('bookNoneKnown') : t('bookMoreLeft', { count: levelTotal - levelKnown.length })} ${bookLevels.has(bookLevel) ? t('bookHowToLearn') : t('bookTrialOnly')}`}
              </li>
            </ul>
          </div>
        </div>
      ) : null}

      {learned ? (
        <div className="craft-modal-backdrop" onClick={() => setLearned(null)} role="presentation">
          <div className="craft-modal craft-modal--learned" role="dialog" aria-modal="true" aria-label={t('learnedTitle')} onClick={(e) => e.stopPropagation()}>
            <span className="craft-level-badge">{t('learnedLevel', { level: learned.level })}</span>
            <h3 className="craft-heading">{t('learnedTitle')}</h3>
            <div className="craft-learned-recipe"><RecipeLine recipe={learned} pct={pct} /></div>
            <div className="craft-actions">
              <button
                type="button"
                className="auth-button auth-button--compact"
                style={authButtonStyle}
                disabled={missingFor(learned).length > 0}
                onClick={() => { if (fillWith(learned)) setLearned(null); }}
              >
                {t('bookPlace')}
              </button>
              <button type="button" className="auth-button auth-button--compact" style={authButtonStyle} onClick={() => setLearned(null)}>
                {t('ok')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Craft;
